import { CleanContentful } from '@stadionhq-v3/clean-contentful';
import { ContentfulImage, ContentfulLogo } from '@stadionhq-v3/contentful-ui';
import {
  FixtureTeam,
  getMatchStatus,
  MATCH_STATUS,
  OptaMatchStatus,
} from '@stadionhq-v3/core';
import { DEFAULT_LOCALE, Locale } from '@core/constants/locales.constants';
import { ArrowRightLight } from '@components/icons';
import { renameKeyFromTitleToText } from '@utils/objects.utils';
import { ButtonProps } from '@stadionhq-v3/base-ui';
import { Asset, Broadcaster, Fixture } from '@graphql/__generated__/graphql';
import { SfaMatchHeaderProps as MatchHeaderProps } from '@components/v3/organisms/sfa-match-header';
import { AblyMutator } from '@stadionhq-v3/ably';
import cloneDeep from 'lodash/cloneDeep';
import { BroadcastersWrapper } from '@components/organisms/broadcasters-wrapper';
import templateConfig from '@templateConfig';
import { generateFullPathname } from '@utils/paths/generateFullPathname.utils';
import { assetTransform, linkTransform } from '../atoms';
import {
  CtaConfigLinks,
  FixtureCtaDictionary,
} from '../layout/getCoreCtaConfig';
import broadcastersWrapperTransform from '../organisms/broadcastersWrapper.transform';

export type ClubStaticProps = Pick<
  FixtureTeam,
  'clubCrest' | 'clubAbbreviation' | 'clubShortName' | 'clubName'
>;

export type ClubLiveProps = Pick<
  FixtureTeam,
  'score' | 'aggregateScore' | 'halfTimeScore' | 'penaltyScore'
>;

export type MatchHeaderStaticProps = Pick<
  MatchHeaderProps,
  | 'competitionName'
  | 'competitionLogo'
  | 'metaMessagingCopy'
  | 'fixtureMetaDescription'
  | 'venue'
  | 'image'
  | 'ctaLinks'
  | 'kickoffTime'
  | 'kickOffUtc'
  | 'date'
  | 'labels'
  | 'referee'
  | 'broadcasters'
  | 'events'
  | 'countdown'
> & {
  away: ClubStaticProps;
  home: ClubStaticProps;
  matchStatus?: OptaMatchStatus;
};

export type MatchHeaderLiveProps = Pick<
  MatchHeaderProps,
  'events' | 'minute' | 'penalties' | 'broadcasters'
> & {
  away: ClubLiveProps;
  home: ClubLiveProps;
  matchStatus: OptaMatchStatus;
};

export type AblyData = {
  header: MatchHeaderLiveProps;
  matchStatus: MatchHeaderLiveProps['matchStatus'];
};

export const matchHeaderAblyMutator =
  (
    staticMatchHeaderData?: MatchHeaderStaticProps
  ): AblyMutator<MatchHeaderLiveProps, AblyData> =>
  ({ state, response }) => {
    const data = response ? cloneDeep(response) : cloneDeep(state);

    const { header, matchStatus } = data as AblyData;

    return {
      ...staticMatchHeaderData,
      ...header,
      matchStatus:
        matchStatus || (staticMatchHeaderData?.matchStatus as OptaMatchStatus),
      home: {
        ...staticMatchHeaderData?.home,
        ...header?.home,
        score: header?.home?.score ?? 0,
      },
      away: {
        ...staticMatchHeaderData?.away,
        ...header?.away,
        score: header?.away?.score ?? 0,
      },
    };
  };

export const matchHeaderTransform = (
  {
    competition,
    awayClub,
    homeClub,
    matchData,
    matchPeriod,
    venue,
    additionalLinks,
    image,
    additionalTextInfo,
    metaDescription,
    city,
    kickOffUtc,
    referee,
    broadcasters,
    dataProviderId,
  }: CleanContentful<Fixture>,
  ctaLabels: FixtureCtaDictionary,
  links: CtaConfigLinks,
  locale: Locale = DEFAULT_LOCALE,
  fallbackClubCrest?: CleanContentful<Asset>
) => {
  const applyButtonStyling = (
    buttons: ButtonProps[],
    maxButtons = 4
  ): ButtonProps[] => {
    const buttonVariants: ButtonProps['variant'][] = [
      'primary',
      'secondary',
      'secondary',
      'tertiary',
    ];

    return buttons
      .filter(Boolean)
      .slice(0, maxButtons) // Limit to max 4 buttons
      .map((button, index) => ({
        ...button,
        variant: buttonVariants[index],
        onDark: true,
      }));
  };

  const getCtaLinks = (): ButtonProps[] => {
    const mappedMatchStatus = getMatchStatus(matchPeriod as OptaMatchStatus);

    const {
      hospitalityLink,
      ticketingLink,
      matchReportLink,
      liveVideoLink,
      liveAudioLink,
    } = links;

    const hospitalityCta =
      hospitalityLink?.url &&
      renameKeyFromTitleToText(
        linkTransform(hospitalityLink, locale, <ArrowRightLight />)
      );

    const ticketingCta =
      ticketingLink?.url &&
      renameKeyFromTitleToText(
        linkTransform(ticketingLink, locale, <ArrowRightLight />)
      );

    const matchReportCta =
      matchReportLink &&
      renameKeyFromTitleToText(
        linkTransform(
          {
            url: matchReportLink,
            title: ctaLabels.viewMatchReport,
          },
          locale,
          <ArrowRightLight />
        )
      );

    const watchLiveCta = liveVideoLink && {
      url: generateFullPathname({
        locale,
        route: templateConfig.navigation.routes.VIDEO_DETAIL,
        params: { slug: links.liveVideoLink },
      }),
      text: ctaLabels.watchLiveStream,
      iconAfter: <ArrowRightLight />,
    };

    const liveAudioCta = liveAudioLink && {
      url: generateFullPathname({
        locale,
        route: templateConfig.navigation.routes.VIDEO_DETAIL,
        params: { slug: links.liveAudioLink },
      }),
      text: ctaLabels.listenToLiveBroadcast,
      iconAfter: <ArrowRightLight />,
    };

    const additionalCtas =
      additionalLinks
        ?.map((link) =>
          renameKeyFromTitleToText(
            linkTransform(link, locale, <ArrowRightLight />)
          )
        )
        .filter(Boolean) ?? [];

    if (mappedMatchStatus === MATCH_STATUS.PRE_MATCH) {
      const buttons = [
        ticketingCta,
        hospitalityCta,
        watchLiveCta,
        liveAudioCta,
        ...additionalCtas,
      ];

      return applyButtonStyling(buttons as ButtonProps[]);
    }

    if (mappedMatchStatus === MATCH_STATUS.LIVE) {
      const buttons = [watchLiveCta, liveAudioCta, ...additionalCtas];

      return applyButtonStyling(buttons as ButtonProps[]);
    }

    if (mappedMatchStatus === MATCH_STATUS.POST_MATCH) {
      const buttons = [matchReportCta, ...additionalCtas];

      return applyButtonStyling(buttons as ButtonProps[]);
    }

    return [];
  };

  const getCompetitionLogo = competition?.logoInverse || competition?.logo;

  const getClubCrest = (
    club:
      | CleanContentful<Fixture>['homeClub']
      | CleanContentful<Fixture>['awayClub']
  ) => {
    const logoAvailable = club?.clubCrestInverse || club?.clubCrest;

    return logoAvailable ? (
      <ContentfulLogo {...logoAvailable} width={184} height={184} />
    ) : fallbackClubCrest ? (
      <ContentfulLogo
        {...assetTransform(fallbackClubCrest)}
        width={184}
        height={184}
      />
    ) : undefined;
  };

  const matchHeader: MatchHeaderStaticProps = {
    competitionName: competition?.name,
    competitionLogo: getCompetitionLogo ? (
      <ContentfulLogo
        {...assetTransform(getCompetitionLogo)}
        height={32}
        width={78}
      />
    ) : undefined,
    away: {
      clubCrest: getClubCrest(awayClub),
      clubName: awayClub?.name,
      clubShortName: awayClub?.shortName,
      clubAbbreviation: awayClub?.abbreviation,
    },
    home: {
      clubCrest: getClubCrest(homeClub),
      clubName: homeClub?.name,
      clubShortName: homeClub?.shortName,
      clubAbbreviation: homeClub?.abbreviation,
    },
    metaMessagingCopy: additionalTextInfo,
    fixtureMetaDescription: metaDescription,
    ctaLinks: getCtaLinks(),
    venue: [venue, city].filter(Boolean).join(', ') || undefined,
    image: image ? <ContentfulImage {...image} /> : undefined,
    matchStatus: matchPeriod as OptaMatchStatus,
    kickOffUtc,
    referee,
    events: matchData?.header?.events,
    broadcasters: (
      <BroadcastersWrapper
        {...broadcastersWrapperTransform(
          locale,
          dataProviderId,
          broadcasters as Broadcaster[]
        )}
      />
    ),
  };

  const matchHeaderLive: MatchHeaderLiveProps = {
    away: {
      score: matchData?.header?.away?.score,
      aggregateScore: matchData?.header?.away?.aggregateScore,
      penaltyScore: matchData?.header?.away?.penaltyScore,
      halfTimeScore: matchData?.header?.away?.halfTimeScore,
    },
    home: {
      score: matchData?.header?.home?.score,
      aggregateScore: matchData?.header?.home?.aggregateScore,
      penaltyScore: matchData?.header?.home?.penaltyScore,
      halfTimeScore: matchData?.header?.home?.halfTimeScore,
    },
    events: matchData?.header?.events,
    minute: matchData?.header?.minute,
    penalties: matchData?.header?.penalties,
    matchStatus: matchData?.matchStatus as OptaMatchStatus,
    broadcasters: (
      <BroadcastersWrapper
        {...broadcastersWrapperTransform(
          locale,
          dataProviderId,
          broadcasters as Broadcaster[]
        )}
      />
    ),
  };

  return {
    matchHeader,
    matchHeaderLive,
  };
};
